/**
 * What is wrong with a field review, said against the row that is wrong.
 *
 * Both Fields screens used to learn this from a refused Save: a red banner
 * beside the button carrying one sentence about one row, on a table of hundreds.
 * Nothing was marked, so finding the row the sentence was about meant reading
 * the sentence, scrolling, and guessing — and because the server stopped at its
 * first complaint, fixing that row only revealed the next one.
 *
 * Two halves, and they are deliberately separate:
 *
 * - `bindingProblems` is the rules, run against the rows on screen as they are
 *   edited. It is a port of `app/domain/binding_problems.py` and has to stay
 *   one: a screen that marked a row the server accepts, or accepted a review
 *   the server refuses, would be worse than the banner it replaces. The backend
 *   stays the authority — this knows only the dictionary the tab was handed.
 * - `problemsFromRefusal` is for the refusals that arrive anyway: a stale
 *   dictionary, a Feed deleted by somebody else, or any 422 these rules do not
 *   model. It maps the server's `details.problems` onto rows **by path and
 *   Feed**, which is why the API sends paths at all.
 *
 * Kept out of the components, like every other rule in this frontend, because
 * `npm test` is bare `node` over `tests/` and a rule inside a component is a
 * rule nothing can assert.
 */

import type { Role } from "./api";

/** The problems these rules can find, and the ones the server can send. */
export type ProblemCode =
  | "stat_without_statistic"
  | "unknown_stat_key"
  | "colliding_column"
  // Server-only, deliberately: a row can be scoped to this profile's Feeds and
  // nothing else, so the local rules have no way to produce it. It arrives
  // through `problemsFromRefusal` when a Feed has gone since the review opened.
  | "unknown_feed"
  // Anything else a 422 carries — a duplicate league and season, a refused
  // weight — which has no row to blame and still has to be shown.
  | (string & {});

/**
 * One row of a Fields table, as these rules need it.
 *
 * `key` is supplied rather than derived because the two screens key their rows
 * differently and both are right: Add Source has no Feeds yet and keys by path,
 * while the review screen keys by Feed and path, since `yards` in the passing
 * report and `yards` in the rushing one are two rows meaning two things.
 */
export interface ProblemRow {
  key: string;
  path: string;
  role: Role;
  statKey: string | null;
  /** The Feed this row is scoped to, or null to read every Feed (ADR-0055). */
  feedId: string | null;
  parseRule: Record<string, unknown>;
}

/** A problem, and which row to mark for it. Null where no row can be blamed. */
export interface RowProblem {
  rowKey: string | null;
  code: ProblemCode;
  message: string;
  field: string | null;
}

/** What the surrounding review is judged against. */
export interface ReviewContext {
  /** The keys this profile's sport declares — `statsQuery` filtered by sport. */
  knownStatKeys: readonly string[];
  /** The profile's Feeds. Empty before the profile exists, which is Add Source. */
  feedIds: readonly string[];
  /** Named in the unknown-key message, because the key usually exists in some
   *  other sport and "not in the dictionary" would read as a typo (ADR-0017). */
  sport: string;
}

/** The server's own account of one problem, as `details.problems` carries it. */
export interface RefusedProblem {
  code: string;
  message: string;
  path: string;
  field: string | null;
  feed_id: string | null;
}

/** As much of an `ApiError` as mapping a refusal needs. */
export interface Refusal {
  code: string;
  message: string;
  field: string | null;
  details: Record<string, unknown>;
}

/**
 * Every problem in the review, in row order, at most one per row.
 *
 * Row order is screen order, so "the first problem" is the topmost one — the
 * same promise the required-field rules make. One per row because two
 * complaints against one row is one of them being noise, and because a row
 * with no statistic has nothing for a collision to be computed from.
 */
export function bindingProblems(
  rows: readonly ProblemRow[],
  context: ReviewContext,
): RowProblem[] {
  const known = new Set(context.knownStatKeys);
  // A profile with no Feeds yet still has one column space to collide in, and
  // it is the one an unscoped row reads. Taking the empty list literally would
  // find no collisions at all on Add Source — which is the screen where the
  // same statistic gets picked twice.
  const reading: (string | null)[] = context.feedIds.length > 0 ? [...context.feedIds] : [null];
  // Keyed by role as well as Feed and key: a statistic and a carried field on
  // one path publish under two different headers — `Yards - Passing` against
  // `passing.yards` — so they are two columns, not a collision.
  const claimed = new Map<string, string>();
  const problems: RowProblem[] = [];

  for (const row of rows) {
    const own = ownProblem(row, known, context);
    if (own) {
      problems.push(own);
      continue;
    }
    if (row.role !== "stat" && row.role !== "carried") continue;

    const collision = claim(row, row.feedId !== null ? [row.feedId] : reading, claimed);
    if (collision) problems.push(collision);
  }

  return problems;
}

/**
 * This row's claim on the columns it publishes, or the clash that stops it.
 *
 * **It gives up on the first clash, leaving this row's remaining keys and Feeds
 * unclaimed.** That is not an optimisation, it is what the server does
 * (`_claim` returns there), and the difference is observable: with `sacks`
 * scoped to one report, an unscoped `sacks`, and a third `sacks` scoped to
 * another report, abandoning the claim leaves the third row *accepted* and
 * carrying on would refuse it. Carrying on is what this did first, and it is
 * the worst of the two failures the module header names — a row the server
 * saves, marked, on a screen that refuses to save a marked row at all.
 */
function claim(
  row: ProblemRow,
  reads: readonly (string | null)[],
  claimed: Map<string, string>,
): RowProblem | null {
  for (const key of keysOf(row)) {
    for (const feedId of reads) {
      const slot = `${row.role} ${feedId ?? ""} ${key}`;
      const first = claimed.get(slot);
      if (first !== undefined) {
        return {
          rowKey: row.key,
          code: "colliding_column",
          message:
            `Both this and ${first} would publish ${key} from the same Feed, so one ` +
            `of the two values would be dropped without being reported. Bind them to ` +
            `different statistics, or scope them to different Feeds.`,
          field: "stat_key",
        };
      }
      claimed.set(slot, row.path);
    }
  }
  return null;
}

/** What is wrong with one row on its own terms. */
function ownProblem(
  row: ProblemRow,
  known: ReadonlySet<string>,
  context: ReviewContext,
): RowProblem | null {
  if (row.role !== "stat") return null;
  if (!row.statKey) {
    // The state every row passes through: the Role is chosen first and the
    // statistic second. Said as itself rather than as a missing dictionary
    // entry, which is what it used to come back as — a statistic called None,
    // and an instruction to go and add one.
    return {
      rowKey: row.key,
      code: "stat_without_statistic",
      message:
        "Set to Statistic with no statistic chosen, so nothing would be published for " +
        "it. Pick one, or set the field to Exclude from ranks to keep it in the raw " +
        "file unscored.",
      field: "stat_key",
    };
  }
  if (!known.has(row.statKey)) {
    return {
      rowKey: row.key,
      code: "unknown_stat_key",
      message:
        `${row.statKey} is not in the ${context.sport} Stat Dictionary. Add it there ` +
        `first so its Direction is declared once for every source in that sport.`,
      field: "stat_key",
    };
  }
  return null;
}

/**
 * Every key one row can publish, before a Feed qualifies it.
 *
 * The port of `ProfileSpec.keys_of`. A carried field publishes under its path;
 * a source packing several numbers into one string publishes one column per
 * component, and the components rather than the `stat_key` are what a collision
 * is computed from.
 */
function keysOf(row: ProblemRow): string[] {
  if (row.role === "carried") return [row.path];
  const rule = row.parseRule ?? {};
  if (rule.type === "split") {
    const components = Array.isArray(rule.components) ? rule.components : [];
    return components.map((component) => String(component));
  }
  return row.statKey ? [row.statKey] : [];
}

/**
 * What a row said when it was submitted, so a mark can outlive it or not.
 *
 * A refusal describes the review at the moment Save was pressed. Painted and
 * left there it becomes a lie about the row the moment the row is corrected —
 * and clearing the whole refusal on the first edit is the other lie, because
 * the three rows nobody touched are still wrong. So a mark survives exactly as
 * long as the row it is about looks the way it did when it was refused.
 *
 * The parse rule is in the signature because it decides what a row publishes:
 * a split rule's components are its columns, so editing one can resolve a
 * collision without any other part of the row moving.
 */
export function rowSignature(row: ProblemRow): string {
  return [row.role, row.statKey ?? "", row.feedId ?? "", JSON.stringify(row.parseRule ?? {})].join(
    " ",
  );
}

/**
 * A refusal, put against the rows that caused it.
 *
 * Matched on path **and** Feed, in that order of preference: a path may be two
 * rows on a multi-report source, and marking the wrong one sends the reader to
 * a row that is fine. A problem whose path matches no row on screen is kept
 * with `rowKey: null` rather than dropped — losing it would answer a refused
 * Save with silence, which is the whole defect being fixed.
 *
 * A refusal carrying no problem list — any 422 these rules do not model, or an
 * API older than they are — becomes one problem with no row, so the caller has
 * exactly one shape to render.
 */
export function problemsFromRefusal(
  refusal: Refusal,
  rows: readonly ProblemRow[],
  /**
   * The signatures the rows had when this refusal was earned, where the caller
   * kept them. A row that has been edited since drops its mark; every row that
   * has not keeps it. Omitted means keep them all, which is right for a screen
   * that has not saved yet.
   */
  submitted?: ReadonlyMap<string, string>,
): RowProblem[] {
  const listed = refusal.details?.problems;
  if (!Array.isArray(listed) || listed.length === 0) {
    return [
      {
        rowKey: null,
        code: refusal.code,
        message: refusal.message,
        field: refusal.field ?? null,
      },
    ];
  }

  const problems: RowProblem[] = [];
  for (const entry of listed) {
    const problem = entry as Partial<RefusedProblem>;
    const path = typeof problem.path === "string" ? problem.path : "";
    const feedId = typeof problem.feed_id === "string" ? problem.feed_id : null;
    const exact = rows.find((row) => row.path === path && row.feedId === feedId);
    const matched = exact ?? rows.find((row) => row.path === path) ?? null;
    // Edited since it was refused: the complaint was about something else, and
    // the live rules speak for the row as it is now.
    if (
      matched &&
      submitted &&
      submitted.get(matched.key) !== undefined &&
      submitted.get(matched.key) !== rowSignature(matched)
    ) {
      continue;
    }
    problems.push({
      rowKey: matched?.key ?? null,
      code: problem.code ?? refusal.code,
      // The server's own words, quoted. Re-deriving *this* message locally
      // would mean guessing which rule produced it from its code, and getting
      // that wrong is a row marked with the wrong reason.
      //
      // That is a different thing from `bindingProblems` writing its own
      // sentences, which it does deliberately: those are addressed to a row
      // ("Both this and …") where the server's are addressed to a request
      // ("'rush.sacks' and 'defense.sacks' …"). The two wordings are allowed to
      // differ; what may not differ is which rows are marked and why, and that
      // is what the parity cases in both suites pin.
      message: problem.message ?? refusal.message,
      field: problem.field ?? null,
    });
  }
  return problems;
}

/**
 * What to say under each row, given both sets of problems.
 *
 * **Live problems win.** They describe the rows as they *are*; a refusal
 * describes the review at the moment Save was pressed. One message per row,
 * because a row carrying two complaints is a row the reader has to read twice
 * to find one answer.
 *
 * Here rather than in the two screens, which each had a copy of this loop, for
 * the reason the header gives: a rule inside a component is a rule nothing can
 * assert.
 */
export function markedRows(
  live: readonly RowProblem[],
  fromRefusal: readonly RowProblem[],
): Map<string, string> {
  const marked = new Map<string, string>();
  for (const problem of [...live, ...fromRefusal]) {
    if (problem.rowKey !== null && !marked.has(problem.rowKey)) {
      marked.set(problem.rowKey, problem.message);
    }
  }
  return marked;
}

/** The row a failed Save travels to: the first one that has a problem. */
export function firstMarkedRow(problems: readonly RowProblem[]): string | null {
  return problems.find((problem) => problem.rowKey !== null)?.rowKey ?? null;
}

/**
 * How a screen announces the rows it has marked.
 *
 * Prose rather than markup, so the one place it is written is a place a test
 * can read — the precedent is `weightNotice` in `stat-weight.ts`, and the
 * server's own `refusal()` counts the same way for the same reason.
 *
 * `blocked` is the review the screen refuses to send; `refused` is what came
 * back from a Save that was sent, where the count is the part the banner must
 * not leave out.
 */
export function markedNotice(count: number): { title: string; detail: string } | null {
  if (count <= 0) return null;
  return {
    title:
      count === 1
        ? "One field cannot be saved as it stands"
        : `${count} fields cannot be saved as they stand`,
    detail: "Each is marked in the table below, under the row it is about.",
  };
}

/** The same thing said about a refusal that has already come back. */
export function refusalNotice(count: number): string | null {
  if (count <= 0) return null;
  return count === 1
    ? "The field it names is marked in the table above."
    : `The ${count} fields it names are marked in the table above.`;
}

/**
 * The control a refusal belongs beside, for the fields that have one.
 *
 * Only the three the Add Source form owns. Everything else — `sport`,
 * `template_key`, `feeds` — is either composed or not a control somebody can
 * put a cursor in, and scrolling to a box that cannot be the problem is worse
 * than scrolling to the message.
 */
export function anchorForField(field: string | null): "name" | "league" | "identity" | null {
  if (field === "name") return "name";
  if (field === "league") return "league";
  // One complaint to a reader: the rows that say who a player is.
  if (field === "identity_key" || field === "identity") return "identity";
  return null;
}
